/**
 * ErrorBoundary — catches render errors in lazy-loaded result components
 * and shows a friendly error card instead of a white screen.
 *
 * Scoped around individual lazy chunks (BiasAuditCard, ChatPanel,
 * AgentTimeline) so a failure in one doesn't take down the whole
 * results area. Most failures in this codebase come from third-party
 * model downloads (cross-encoder chunk failing over a spotty network)
 * or Zod schema mismatches from a misbehaving LLM response.
 */

import { Component, type ErrorInfo, type ReactNode } from "react";
import { AlertTriangle } from "lucide-react";

interface Props {
  children: ReactNode;
  label: string;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error(`[ErrorBoundary:${this.props.label}]`, error, info);
  }

  override render(): ReactNode {
    if (this.state.error) {
      return (
        <div className="ta-card p-6 border-rose-500/30 bg-rose-500/5">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-4 h-4 text-rose-400 mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-rose-300 mb-1">
                {this.props.label} failed to load
              </div>
              <div className="text-xs text-slate-400 leading-relaxed">
                The rest of the match result is unaffected. Retry by refreshing
                the page; if it keeps happening, check the browser console or
                your network connection.
              </div>
              <div className="text-[10px] font-mono text-slate-600 mt-2 truncate">
                {this.state.error.message}
              </div>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
